import {Inject, Injectable} from "@angular/core";
import {AUTH_ERROR_QUERY_PARAM, SSO_POST_LOGIN_URL_STORAGE_KEY} from "../../constants/sso.constants";

// Shared by every redirect-based login flow (Auth0 SSO, Google/Microsoft social login) -
// each one unloads the page to hand off to an identity provider, so the in-memory
// "where was the user headed" state (StateService) has to be persisted somewhere that
// survives the round trip, and read back the same way regardless of which flow ran.
@Injectable({providedIn: 'root'})
export class PostLoginRedirectService {

  constructor(@Inject('stateService') private stateService: any,
              @Inject('$state') private $state: any) {
  }

  // Call right before redirecting away to an identity provider.
  capturePreviousState(): void {
    const previousState = this.stateService.getPreviousState();
    if (previousState) {
      const previousStateUrl = this.$state.href(previousState, this.stateService.getPreviousStateParams());
      sessionStorage.setItem(SSO_POST_LOGIN_URL_STORAGE_KEY, previousStateUrl);
    } else {
      sessionStorage.removeItem(SSO_POST_LOGIN_URL_STORAGE_KEY);
    }
  }

  // Call after a successful login callback.
  navigateToPostLoginUrl(): void {
    const postLoginUrl = sessionStorage.getItem(SSO_POST_LOGIN_URL_STORAGE_KEY);
    sessionStorage.removeItem(SSO_POST_LOGIN_URL_STORAGE_KEY);
    window.location.href = this.isSameOriginRelativePath(postLoginUrl)
      ? window.location.origin + postLoginUrl
      : window.location.origin;
  }

  // Defense in depth: postLoginUrl always comes from $state.href() on this app's own
  // registered states, so it can't itself be a foreign-origin URL - but this guards
  // against a hard navigation to an attacker-controlled origin if sessionStorage were
  // ever tampered with by something else running on the page. A single leading '/' is
  // required, and a second leading '/' or '\' (both of which browsers can normalize
  // into a scheme-relative "//evil.com" URL) is rejected.
  private isSameOriginRelativePath(url: string | null): url is string {
    return !!url && url.startsWith('/') && !/^\/[\\/]/.test(url);
  }

  // Call after a failed login callback - sends the user back to the login page with a
  // flag login-view uses to surface an error toast.
  navigateToLoginWithError(): void {
    sessionStorage.removeItem(SSO_POST_LOGIN_URL_STORAGE_KEY);
    window.location.href = `${window.location.origin}/login?${AUTH_ERROR_QUERY_PARAM}=1`;
  }
}
